import {
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
} from "react";
import { createPortal } from "react-dom";
import type {
  CompareMode,
  DashboardFilters,
  DatePreset,
} from "../shared/contracts";
import { useI18n, type LanguageCode } from "../i18n";
import {
  MAX_RANGE_DAYS,
  addDays,
  computeGranularity,
  daysInclusive,
  getComparisonRange,
  getDaysInMonth,
  getMonthOffset,
  getPresetRange,
  getTodayInTimeZone,
  normalizeDateOrder,
  normalizeTimeZone,
  prepareDashboardDateFilters,
  startOfWeek,
} from "../shared/date-ranges";

type ActiveRange = "primary" | "compare";
type SelectingEdge = "start" | "end";
const SWAPPED_DATE_NOTICE = "swapped_date_range";

interface PickerState {
  open: boolean;
  draft: DashboardFilters;
  visibleMonth: string;
  selecting: SelectingEdge;
  notice: string | null;
}

type PickerAction =
  | { type: "OPEN_PICKER"; filters: DashboardFilters }
  | { type: "SELECT_PRESET"; preset: DatePreset; now: Date }
  | {
      type: "SET_DATES";
      target: ActiveRange;
      startDate: string;
      endDate: string;
      complete?: boolean;
    }
  | { type: "TOGGLE_COMPARE"; enabled: boolean }
  | { type: "SET_COMPARE_MODE"; mode: CompareMode }
  | { type: "SET_VISIBLE_MONTH"; month: string }
  | { type: "APPLY_FILTERS"; filters: DashboardFilters }
  | { type: "CANCEL_FILTERS"; filters: DashboardFilters };

export function DateRangePicker({
  value,
  disabled = false,
  maxRangeDays = MAX_RANGE_DAYS,
  onApply,
}: {
  value: DashboardFilters;
  disabled?: boolean;
  maxRangeDays?: number;
  onApply: (filters: DashboardFilters) => void;
}) {
  const { text, locale, language } = useI18n();
  const [state, dispatch] = useReducer(pickerReducer, value, createState);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [popoverStyle, setPopoverStyle] = useState<CSSProperties>({});
  const prepared = useMemo(
    () => prepareDashboardDateFilters(state.draft, { maxRangeDays }),
    [maxRangeDays, state.draft],
  );
  const summary = formatCompactSummary(value, text, locale);
  const draftSummary = formatCompactSummary(prepared.filters, text, locale);
  const selectedTimezone = state.draft.timezone || "Atlantic/Canary";
  const today = getTodayInTimeZone(
    selectedTimezone,
    new Date(),
  );
  const applyDisabled = prepared.errors.length > 0;

  useEffect(() => {
    if (!state.open) return;

    function updatePosition() {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      if (viewportWidth <= 560) {
        setPopoverStyle({});
        return;
      }

      const margin = viewportWidth <= 840 ? 14 : 24;
      const availableWidth =
        viewportWidth <= 840 ? viewportWidth - margin * 2 : viewportWidth - 280;
      const width = Math.min(880, Math.max(320, availableWidth));
      const top = Math.min(rect.bottom + 8, window.innerHeight - 80);

      if (viewportWidth <= 840) {
        setPopoverStyle({
          top,
          left: margin,
          width,
          maxHeight: Math.max(420, window.innerHeight - top - margin),
        });
        return;
      }

      setPopoverStyle({
        top,
        right: Math.max(margin, viewportWidth - rect.right),
        width,
        maxHeight: Math.max(420, window.innerHeight - top - margin),
      });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [state.open]);

  function openPicker() {
    dispatch({ type: "OPEN_PICKER", filters: value });
  }

  function cancelPicker() {
    dispatch({ type: "CANCEL_FILTERS", filters: value });
  }

  function applyPicker() {
    if (applyDisabled) return;
    onApply(prepared.filters);
    dispatch({ type: "APPLY_FILTERS", filters: prepared.filters });
  }

  return (
    <div className="date-picker-shell">
      <button
        ref={buttonRef}
        type="button"
        className="date-summary-button"
        onClick={openPicker}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={state.open}
      >
        <CalendarDays size={15} />
        <span>
          <strong>{summary.primary}</strong>
          {summary.compare ? <small>{summary.compare}</small> : null}
        </span>
        <ChevronDown size={14} />
      </button>

      {state.open && typeof document !== "undefined"
        ? createPortal(
        <div
          className="date-picker-popover"
          role="dialog"
          aria-modal="false"
          aria-label={text.date.dialogLabel}
          style={popoverStyle}
        >
          <div className="date-picker-layout">
            <aside className="date-presets" aria-label={text.date.presetsLabel}>
              {Object.entries(text.date.presets).map(([preset, label]) => (
                <button
                  type="button"
                  key={preset}
                  className={
                    state.draft.datePreset === preset ? "active" : ""
                  }
                  onClick={() =>
                    dispatch({
                      type: "SELECT_PRESET",
                      preset: preset as DatePreset,
                      now: new Date(),
                    })
                  }
                >
                  {label}
                </button>
              ))}
            </aside>

            <section className="date-picker-main">
              <div className="date-picker-toolbar">
                <button
                  type="button"
                  className="icon-button"
                  onClick={() =>
                    dispatch({
                      type: "SET_VISIBLE_MONTH",
                      month: getMonthOffset(state.visibleMonth, -1),
                    })
                  }
                  title={text.date.previousMonth}
                  aria-label={text.date.previousMonth}
                >
                  <ChevronLeft size={16} />
                </button>
                <div>
                  <strong>{draftSummary.primary}</strong>
                  {draftSummary.compare ? (
                    <span>{draftSummary.compare}</span>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() =>
                    dispatch({
                      type: "SET_VISIBLE_MONTH",
                      month: getMonthOffset(state.visibleMonth, 1),
                    })
                  }
                  title={text.date.nextMonth}
                  aria-label={text.date.nextMonth}
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className="date-input-grid primary-date-inputs">
                <label>
                  <span>{text.date.start}</span>
                  <input
                    type="date"
                    value={state.draft.startDate}
                    max={today}
                    onChange={(event) =>
                      dispatch({
                        type: "SET_DATES",
                        target: "primary",
                        startDate: event.target.value,
                        endDate: state.draft.endDate,
                        complete: true,
                      })
                    }
                  />
                </label>
                <label>
                  <span>{text.date.end}</span>
                  <input
                    type="date"
                    value={state.draft.endDate}
                    max={today}
                    onChange={(event) =>
                      dispatch({
                        type: "SET_DATES",
                        target: "primary",
                        startDate: state.draft.startDate,
                        endDate: event.target.value,
                        complete: true,
                      })
                    }
                  />
                </label>
              </div>

              <div className="calendar-grid">
                <CalendarMonth
                  month={getMonthOffset(state.visibleMonth, -1)}
                  filters={state.draft}
                  today={today}
                  locale={locale}
                  language={language}
                  onSelect={(date) => selectCalendarDate(date, state, dispatch)}
                />
                <CalendarMonth
                  month={state.visibleMonth}
                  filters={state.draft}
                  today={today}
                  locale={locale}
                  language={language}
                  onSelect={(date) => selectCalendarDate(date, state, dispatch)}
                />
              </div>

              <div className="compare-box">
                <label className="compare-toggle">
                  <input
                    type="checkbox"
                    checked={Boolean(state.draft.compareEnabled)}
                    onChange={(event) =>
                      dispatch({
                        type: "TOGGLE_COMPARE",
                        enabled: event.target.checked,
                      })
                    }
                  />
                  <span>{text.date.compare}</span>
                </label>
                {state.draft.compareEnabled ? (
                  <>
                    <label>
                      <span>{text.date.mode}</span>
                      <select
                        value={state.draft.compareMode || "previous_period"}
                        onChange={(event) =>
                          dispatch({
                            type: "SET_COMPARE_MODE",
                            mode: event.target.value as CompareMode,
                          })
                        }
                      >
                        {Object.entries(text.date.compareModes).map(
                          ([mode, label]) => (
                            <option key={mode} value={mode}>
                              {label}
                            </option>
                          ),
                        )}
                      </select>
                    </label>
                    <span className="compare-range-summary">
                      {state.draft.compareStartDate &&
                      state.draft.compareEndDate
                        ? `${formatShortDate(
                            state.draft.compareStartDate,
                            locale,
                          )} - ${formatShortDate(
                            state.draft.compareEndDate,
                            locale,
                          )}`
                        : text.date.compareRange}
                    </span>
                  </>
                ) : null}
              </div>

              {state.draft.compareEnabled &&
              state.draft.compareMode === "custom" ? (
                <div className="date-input-grid compare-inputs">
                  <label>
                    <span>{text.date.compareStart}</span>
                    <input
                      type="date"
                      value={state.draft.compareStartDate || ""}
                      max={today}
                      onChange={(event) =>
                        dispatch({
                          type: "SET_DATES",
                          target: "compare",
                          startDate: event.target.value,
                          endDate:
                            state.draft.compareEndDate || event.target.value,
                          complete: true,
                        })
                      }
                    />
                  </label>
                  <label>
                    <span>{text.date.compareEnd}</span>
                    <input
                      type="date"
                      value={state.draft.compareEndDate || ""}
                      max={today}
                      onChange={(event) =>
                        dispatch({
                          type: "SET_DATES",
                          target: "compare",
                          startDate:
                            state.draft.compareStartDate || event.target.value,
                          endDate: event.target.value,
                          complete: true,
                        })
                      }
                    />
                  </label>
                </div>
              ) : null}

              {state.notice ? (
                <div className="date-notice" role="status">
                  {state.notice === SWAPPED_DATE_NOTICE
                    ? text.date.swappedNotice
                    : state.notice}
                </div>
              ) : null}
              {prepared.errors.length ? (
                <div className="date-errors" role="alert">
                  {prepared.errors.map((error) => (
                    <span key={error}>{formatDateError(error, language)}</span>
                  ))}
                </div>
              ) : null}
            </section>
          </div>

          <footer className="date-picker-footer">
            <span>{text.date.maxHistory}</span>
            <div>
              <button
                type="button"
                className="secondary-button"
                onClick={cancelPicker}
              >
                <X size={14} />
                {text.date.cancel}
              </button>
              <button
                type="button"
                className="secondary-button apply-date-button"
                disabled={applyDisabled}
                title={
                  applyDisabled
                    ? formatDateError(prepared.errors[0], language) ||
                      text.date.invalidDates
                    : text.date.applyFilters
                }
                onClick={applyPicker}
              >
                <Check size={14} />
                {text.date.apply}
              </button>
            </div>
          </footer>
        </div>,
          document.body,
        )
        : null}
    </div>
  );
}

function createState(value: DashboardFilters): PickerState {
  return {
    open: false,
    draft: value,
    visibleMonth: monthStart(value.endDate),
    selecting: "start",
    notice: null,
  };
}

function pickerReducer(
  state: PickerState,
  action: PickerAction,
): PickerState {
  switch (action.type) {
    case "OPEN_PICKER":
      return {
        ...createState(action.filters),
        open: true,
      };
    case "CANCEL_FILTERS":
      return createState(action.filters);
    case "APPLY_FILTERS":
      return createState(action.filters);
    case "SELECT_PRESET": {
      const timezone = normalizeTimeZone(state.draft.timezone);
      const range = getPresetRange(
        action.preset,
        timezone,
        action.now,
        state.draft,
      );
      return updatePrimaryRange(state, range, {
        datePreset: action.preset,
        notice: null,
      });
    }
    case "SET_DATES": {
      const order = normalizeDateOrder(action.startDate, action.endDate);
      const notice = order.swapped ? SWAPPED_DATE_NOTICE : null;
      if (action.target === "compare") {
        return {
          ...state,
          draft: {
            ...state.draft,
            compareMode: "custom",
            compareEnabled: true,
            compareStartDate: order.range.startDate,
            compareEndDate: order.range.endDate,
          },
          selecting: action.complete
            ? "start"
            : state.selecting === "start"
              ? "end"
              : "start",
          notice,
        };
      }
      return updatePrimaryRange(state, order.range, {
        datePreset: "custom",
        notice,
        selecting: action.complete
          ? "start"
          : state.selecting === "start"
            ? "end"
            : "start",
      });
    }
    case "TOGGLE_COMPARE": {
      if (!action.enabled) {
        const next = { ...state.draft, compareEnabled: false };
        delete next.compareStartDate;
        delete next.compareEndDate;
        return {
          ...state,
          draft: next,
          notice: null,
        };
      }
      const primary = {
        startDate: state.draft.startDate,
        endDate: state.draft.endDate,
      };
      const compare = getComparisonRange(
        primary,
        state.draft.compareMode || "previous_period",
        {
          startDate: state.draft.compareStartDate,
          endDate: state.draft.compareEndDate,
        },
      );
      return {
        ...state,
        draft: {
          ...state.draft,
          compareEnabled: true,
          compareMode: state.draft.compareMode || "previous_period",
          compareAlignBy: state.draft.compareAlignBy || "date_index",
          compareStartDate: compare?.startDate,
          compareEndDate: compare?.endDate,
        },
        notice: null,
      };
    }
    case "SET_COMPARE_MODE": {
      const primary = {
        startDate: state.draft.startDate,
        endDate: state.draft.endDate,
      };
      const compare = getComparisonRange(primary, action.mode, {
        startDate: state.draft.compareStartDate,
        endDate: state.draft.compareEndDate,
      });
      return {
        ...state,
        draft: {
          ...state.draft,
          compareEnabled: true,
          compareMode: action.mode,
          compareStartDate: compare?.startDate,
          compareEndDate: compare?.endDate,
        },
        notice: null,
      };
    }
    case "SET_VISIBLE_MONTH":
      return { ...state, visibleMonth: action.month };
    default:
      return state;
  }
}

function updatePrimaryRange(
  state: PickerState,
  range: { startDate: string; endDate: string },
  options: {
    timezone?: string;
    datePreset?: DatePreset;
    notice: string | null;
    selecting?: SelectingEdge;
  },
) {
  const compare = state.draft.compareEnabled
    ? getComparisonRange(
        range,
        state.draft.compareMode || "previous_period",
        state.draft.compareMode === "custom"
          ? {
              startDate: state.draft.compareStartDate,
              endDate: state.draft.compareEndDate,
            }
          : undefined,
      )
    : null;
  const next: DashboardFilters = {
    ...state.draft,
    timezone: options.timezone || state.draft.timezone,
    datePreset: options.datePreset || "custom",
    startDate: range.startDate,
    endDate: range.endDate,
    granularity: computeGranularity(range.startDate, range.endDate),
  };
  if (compare) {
    next.compareStartDate = compare.startDate;
    next.compareEndDate = compare.endDate;
  }
  return {
    ...state,
    draft: next,
    visibleMonth: monthStart(range.endDate),
    selecting: options.selecting || state.selecting,
    notice: options.notice,
  };
}

function selectCalendarDate(
  date: string,
  state: PickerState,
  dispatch: Dispatch<PickerAction>,
) {
  dispatch({
    type: "SET_DATES",
    target: "primary",
    startDate: state.selecting === "start" ? date : state.draft.startDate,
    endDate: state.selecting === "end" ? date : state.draft.endDate,
  });
}

function CalendarMonth({
  month,
  filters,
  today,
  locale,
  language,
  onSelect,
}: {
  month: string;
  filters: DashboardFilters;
  today: string;
  locale: string;
  language: LanguageCode;
  onSelect: (date: string) => void;
}) {
  const days = buildMonthGrid(month);
  const label = formatMonth(month, locale);
  const weekdayLabels =
    language === "es"
      ? ["L", "M", "X", "J", "V", "S", "D"]
      : ["M", "T", "W", "T", "F", "S", "S"];
  return (
    <div className="calendar-month" role="group" aria-label={label}>
      <strong>{label}</strong>
      <div className="weekday-row" aria-hidden="true">
        {weekdayLabels.map((day, index) => (
          <span key={`${day}-${index}`}>{day}</span>
        ))}
      </div>
      <div className="day-grid" role="grid" aria-label={label}>
        {days.map((date, index) =>
          date ? (
            <button
              type="button"
              key={date}
              role="gridcell"
              disabled={date > today}
              className={dayClass(date, filters)}
              aria-label={formatLongDate(date, locale)}
              aria-pressed={isRangeEdge(date, filters)}
              onClick={() => onSelect(date)}
            >
              {Number(date.slice(-2))}
            </button>
          ) : (
            <span key={`blank-${index}`} role="presentation" />
          ),
        )}
      </div>
    </div>
  );
}

function buildMonthGrid(month: string) {
  const monthStartDate = monthStart(month);
  const daysInMonth = getDaysInMonth(monthStartDate);
  const firstWeekStart = startOfWeek(monthStartDate);
  const blanks = daysInclusive(firstWeekStart, monthStartDate) - 1;
  const days: Array<string | null> = Array.from({ length: blanks }, () => null);
  for (let index = 0; index < daysInMonth; index += 1) {
    days.push(addDays(monthStartDate, index));
  }
  return days;
}

function dayClass(date: string, filters: DashboardFilters) {
  const classes = ["day-button"];
  if (date >= filters.startDate && date <= filters.endDate) {
    classes.push("in-primary");
  }
  if (
    filters.compareEnabled &&
    filters.compareStartDate &&
    filters.compareEndDate &&
    date >= filters.compareStartDate &&
    date <= filters.compareEndDate
  ) {
    classes.push("in-compare");
  }
  if (isRangeEdge(date, filters)) classes.push("range-edge");
  return classes.join(" ");
}

function isRangeEdge(date: string, filters: DashboardFilters) {
  return [
    filters.startDate,
    filters.endDate,
    filters.compareStartDate,
    filters.compareEndDate,
  ].includes(date);
}

function monthStart(date: string) {
  return `${date.slice(0, 7)}-01`;
}

function formatMonth(date: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(new Date(`${date}T12:00:00`));
}

function formatLongDate(date: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "full",
  }).format(new Date(`${date}T12:00:00`));
}

function formatCompactSummary(
  filters: DashboardFilters,
  text: ReturnType<typeof useI18n>["text"],
  locale: string,
) {
  const primary = `${formatShortDate(filters.startDate, locale)} - ${formatShortDate(
    filters.endDate,
    locale,
  )}`;
  const compare =
    filters.compareEnabled && filters.compareStartDate && filters.compareEndDate
      ? `${text.date.vs} ${formatShortDate(
          filters.compareStartDate,
          locale,
        )} - ${formatShortDate(
          filters.compareEndDate,
          locale,
        )} (${text.date.compareModes[filters.compareMode || "previous_period"]})`
      : null;
  return { primary, compare };
}

function formatShortDate(date: string, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(`${date}T12:00:00`));
}

function formatDateError(error: string | undefined, language: LanguageCode) {
  if (!error) return "";
  if (language === "es") return error;
  if (error.includes("fechas válidas")) {
    return "The primary range must have valid dates.";
  }
  if (error.includes("comparación personalizado")) {
    return "The custom comparison range is incomplete.";
  }
  if (error.includes("fechas futuras")) {
    return error.startsWith("El rango de comparación")
      ? "The comparison range cannot include future dates."
      : "The primary range cannot include future dates.";
  }
  const limit = error.match(/límite de (\d+) días/);
  if (limit) {
    return error.startsWith("El rango de comparación")
      ? `The comparison range exceeds the ${limit[1]}-day limit.`
      : `The primary range exceeds the ${limit[1]}-day limit.`;
  }
  return error;
}
